import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import isFunction from 'lodash/isFunction';
import { FC, useEffect, useState, useRef, useContext } from 'react';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DroppableProvided,
  DraggableProvided,
  DraggableStateSnapshot
} from 'react-beautiful-dnd';
// Components`
import {
  Box,
  Grid,
  Table as MaUTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  IconButton
} from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import {
  Row,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable
} from 'react-table';
import { Pagination } from './Pagination';
import { theme } from '../../styles/theme';
import { DEFAULT_PER_PAGE_OPTIONS, scrollToTopSticky } from '../../helpers';
import { GlobalContext } from '../../context/global';

export interface ITableColumn {
  accessor: string | ((row: any) => any);
  id?: string;
  Header: string;
  canFilter?: boolean;
  className?: string;
  sort?: boolean;
  isServerSorted?: boolean;
  isServerSortedDesc?: boolean;
  handleClickColumn?: (columnId: any) => void;
  hideLoad?: boolean;
  overrideWidth?: number;
  filterType?: 'input' | 'autocomplete';
  isDate?: boolean;
  isNumber?: boolean;
  isRate?: boolean;
  title?: string;
  Cell?: (data: any) => JSX.Element;
}

interface ITableProps {
  mobileBreakPoint?: number;
  data: any[];
  columns: ITableColumn[];
  noResultsText?: string;
  isLoading?: boolean;
  rowOnClick?: (e: any, val?: any) => void;
  hideDeleted?: boolean;
  cellClasses?: any;
  rowClasses?: any;
  useTableProps?: { [key: string]: any };
  hidePagination?: boolean;
  stickyHeaders?: boolean;
  ResponsiveComponent?: FC<any>;
  ResponsiveComponentLoader?: FC<any>;
  containerClasses?: string;
  tableSize?: 'medium' | 'small';
  onRowsPerPageChange?: (rows: number) => void;
  LoadMoreComponent?: FC<any>;
  mobileProps?: any;
  hover?: boolean;
  maxHeight?: string;
  onDragEnd?: (result: DropResult) => void;
  isDragDisabled?: boolean;
  labelRowsPerPage?: string;
  rowsPerPageOptions?: number[];
  rowsPerPage?: number;
  setRowsPerPage?: (rows: number) => void;
  isUnderContractPage?: boolean;
  scrollToTopOnPaging?: boolean;
}

export const Table: FC<ITableProps> = ({
  mobileBreakPoint,
  columns,
  data,
  isLoading,
  noResultsText = 'No Results',
  hideDeleted,
  cellClasses = '',
  rowClasses,
  useTableProps = {},
  hidePagination,
  stickyHeaders = false,
  rowOnClick,
  ResponsiveComponent,
  containerClasses = '',
  ResponsiveComponentLoader,
  tableSize = 'medium',
  onRowsPerPageChange,
  LoadMoreComponent,
  mobileProps,
  maxHeight,
  hover = false,
  onDragEnd,
  isDragDisabled = true,
  labelRowsPerPage,
  rowsPerPageOptions,
  rowsPerPage,
  setRowsPerPage,
  isUnderContractPage = false,
  scrollToTopOnPaging = false
}) => {
  const {
    getTableProps,
    headerGroups,
    prepareRow,
    page,
    gotoPage,
    setPageSize,
    state: { pageIndex, pageSize }
  } = useTable(
    {
      // @ts-ignore
      columns,
      data,
      userPageCount: 1, // ignored if manualPagination is false
      manualPagination: hidePagination,
      autoResetPage: isLoading, // reset page when loading
      autoResetSortBy: isLoading, // reset sort when loading
      initialState: {
        pageSize: rowsPerPage || DEFAULT_PER_PAGE_OPTIONS[1]
      },
      ...useTableProps
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect
  );
  const [isSticky, setIsSticky] = useState(false);
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const contentRef = useContext(GlobalContext).contentRef;

  useEffect(() => {
    if (stickyHeaders) {
      const handleScroll = () => {
        if (theadRef.current) {
          const rect = theadRef.current.getBoundingClientRect();

          setIsSticky(rect.top <= 100);
        }
      };

      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [stickyHeaders]);

  const handleReturnToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleScrollToTopSticky = () => {
    setTimeout(() => {
      scrollToTopSticky(contentRef);
    }, 500);
  };

  const handleChangePage = (tablePage: number) => {
    gotoPage(tablePage);
    scrollToTopOnPaging && handleScrollToTopSticky();
  };

  const handleChangeRowsPerPage = (value: number) => {
    setPageSize(value);
    scrollToTopOnPaging && handleScrollToTopSticky();
    if (onRowsPerPageChange) {
      onRowsPerPageChange(value);
    }
    if (setRowsPerPage) {
      setRowsPerPage(value);
    }
  };

  // update page size if we hide pagination
  useEffect(() => {
    if (hidePagination && Array.isArray(data)) {
      setPageSize(Number(data.length));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  // always start on first page if data length changes, ie; filter was applied
  useEffect(() => {
    handleChangePage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length]);

  const classes = useStyles({ isUnderContractPage });
  const isDesktop = useMediaQuery(`(min-width: ${mobileBreakPoint || '960'}px)`);
  const isPrint = useMediaQuery('print');

  const getCellStyle = (column: ITableColumn, type: 'header' | 'cell', isDragging?: boolean) => {
    let style: any = { cursor: 'inherit' };

    if (isDragging) {
      style = {
        // width: '25%'
      };
    }
    if (column.isDate) {
      style = {
        ...style,
        textAlign: 'center'
      };
    } else if (column.isNumber) {
      style = {
        ...style,
        textAlign: 'right',
        paddingRight: '3rem'
      };
    } else if (column.isRate) {
      style = {
        ...style,
        textAlign: 'right'
      };
    }

    if (column.overrideWidth) {
      style = {
        ...style,
        width: column.overrideWidth,
        maxWidth: column.overrideWidth,
        wordWrap: 'break-word', // IE11
        overflowWrap: 'break-word'
      };
    }

    if (type === 'header') {
      style = {
        ...style,
        fontWeight: 'bold',
        cursor: column.handleClickColumn ? 'pointer' : 'inherit',
        color: theme.palette.typography.tableHeaders
      };
    }

    return style;
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <TableContainer
        style={maxHeight && { maxHeight: maxHeight }}
        className={clsx(
          classes.printClass,
          containerClasses,
          stickyHeaders ? classes.stickyHeaders : ''
        )}
      >
        <MaUTable
          stickyHeader={stickyHeaders}
          size={tableSize}
          {...getTableProps()}
          style={ResponsiveComponent && !isLoading ? { display: 'block' } : undefined}
        >
          {isDesktop || !ResponsiveComponent || isPrint ? (
            <>
              <TableHead
                ref={theadRef}
                className={isSticky && stickyHeaders ? classes.applyStickyHeaders : ''}
              >
                {headerGroups.map((headerGroup: any) => (
                  <TableRow {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column: any, i) => {
                      return (
                        <TableCell
                          key={`header-cell-${i}`}
                          {...((column as ITableColumn).sort !== false
                            ? column.getHeaderProps(column.getSortByToggleProps())
                            : column.getHeaderProps())}
                          title={column.title || ''}
                          className={clsx(
                            (column as ITableColumn).className,
                            column.headerClassName
                          )}
                          style={getCellStyle(column as ITableColumn, 'header')}
                          onClick={e => {
                            const { handleClickColumn, sort } = column as ITableColumn;
                            const sortProps: any = column.getHeaderProps(
                              column.getSortByToggleProps()
                            );

                            // If the column is sortable and there's an onClick handler, then call it
                            const { onClick } = sortProps || {};
                            sort !== false && onClick && onClick(e);

                            // Also run column click handler if passed
                            handleClickColumn && handleClickColumn(column.id);
                          }}
                        >
                          {(column as ITableColumn).sort !== false ||
                          (column as ITableColumn).isServerSorted ? (
                            <TableSortLabel
                              active={column.isSorted || (column as ITableColumn).isServerSorted}
                              direction={
                                column.isSortedDesc || (column as ITableColumn).isServerSortedDesc
                                  ? 'desc'
                                  : 'asc'
                              }
                            >
                              {column.render('Header')}
                            </TableSortLabel>
                          ) : (
                            column.render('Header')
                          )}
                        </TableCell>
                      );
                    })}
                    {isSticky && stickyHeaders && (
                      <IconButton
                        onClick={handleReturnToTop}
                        size="small"
                        className={classes.returnToTopButton}
                      >
                        <ArrowUpwardIcon
                          style={{
                            color: 'white',
                            backgroundColor: theme.palette.primary.main,
                            borderRadius: '50%'
                          }}
                        />
                      </IconButton>
                    )}
                  </TableRow>
                ))}
              </TableHead>
              <Droppable droppableId="table">
                {(droppableProvided: DroppableProvided) => (
                  <TableBody
                    ref={ref => {
                      droppableProvided.innerRef(ref);
                    }}
                    {...droppableProvided.droppableProps}
                  >
                    {isLoading
                      ? new Array(pageSize || 3).fill('').map((x, j) => (
                          <TableRow
                            key={`table-row-skeleton-${j}`}
                            hover={hover}
                            {...(rowOnClick ? { onClick: rowOnClick } : {})}
                          >
                            {columns.map((column, i) => (
                              <TableCell
                                key={`skeleton-cell-${i}`}
                                style={getCellStyle(column as ITableColumn, 'cell')}
                                className={isFunction(cellClasses) ? cellClasses({}) : cellClasses}
                              >
                                {column.hideLoad ? null : <Skeleton />}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      : page.map((row: Row, i: any) => {
                          prepareRow(row);
                          return !hideDeleted ||
                            (hideDeleted && !(row.original as any).isDeleted) ? (
                            <Draggable
                              draggableId={row.id}
                              index={row.index}
                              key={`${i}`}
                              isDragDisabled={
                                isDragDisabled || (row.original as any)?.isDragDisabled
                              }
                            >
                              {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                                <TableRow
                                  {...row.getRowProps()}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  ref={provided.innerRef}
                                  hover={hover}
                                  className={
                                    isFunction(rowClasses) ? rowClasses(row.original) : rowClasses
                                  }
                                  {...(rowOnClick
                                    ? { onClick: (e: any) => rowOnClick(e, row.original) }
                                    : {})}
                                  sx={{
                                    backgroundColor: theme =>
                                      snapshot.isDragging ? theme.palette.grey[100] : 'none',
                                    display: snapshot.isDragging ? 'table' : undefined
                                  }}
                                >
                                  {row.cells.map((cell: any, j: any) => {
                                    return (
                                      <TableCell
                                        {...cell.getCellProps()}
                                        className={clsx(
                                          (cell.column as ITableColumn).className ?? '',
                                          isFunction(cellClasses)
                                            ? cellClasses(row.original)
                                            : cellClasses,
                                          cell.column.className || ''
                                        )}
                                        style={getCellStyle(
                                          cell.column as ITableColumn,
                                          'cell',
                                          snapshot.isDragging
                                        )}
                                      >
                                        {cell.render('Cell')}
                                      </TableCell>
                                    );
                                  })}
                                </TableRow>
                              )}
                            </Draggable>
                          ) : null;
                        })}
                    {droppableProvided.placeholder}
                  </TableBody>
                )}
              </Droppable>
            </>
          ) : (
            <TableBody className={isLoading ? undefined : classes.mobileTable}>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    className={clsx(
                      classes.mobileCell,
                      isFunction(cellClasses) ? cellClasses({}) : cellClasses
                    )}
                  >
                    {ResponsiveComponentLoader ? <ResponsiveComponentLoader /> : <Skeleton />}
                  </TableCell>
                </TableRow>
              ) : (
                page.map((row: any, i: any) => {
                  prepareRow(row);
                  return !hideDeleted || (hideDeleted && !(row.original as any).isDeleted) ? (
                    <TableRow
                      {...row.getRowProps()}
                      className={isFunction(rowClasses) ? rowClasses(row.original) : rowClasses}
                      {...(rowOnClick ? { onClick: (e: any) => rowOnClick(e, row.original) } : {})}
                      // @ts-ignore
                      style={ResponsiveComponent ? { display: 'block' } : undefined}
                    >
                      <TableCell
                        key={`row-${i}-mobile-cell`}
                        className={clsx(
                          classes.mobileCell,
                          isFunction(cellClasses) ? cellClasses(row.original) : cellClasses
                        )}
                        // @ts-ignore
                        style={ResponsiveComponent ? { display: 'block' } : undefined}
                      >
                        <ResponsiveComponent
                          key={`responsive-row-${i}`}
                          {...row}
                          {...(useTableProps ? useTableProps : {})}
                          {...mobileProps}
                        />
                      </TableCell>
                    </TableRow>
                  ) : null;
                })
              )}
            </TableBody>
          )}
        </MaUTable>
        {!isLoading && Array.isArray(data) && data.length > 0 && !hidePagination && (
          <Box displayPrint="none">
            <Pagination
              rowsPerPageOptions={rowsPerPageOptions ?? DEFAULT_PER_PAGE_OPTIONS}
              count={isLoading ? 0 : data.length}
              rowsPerPage={pageSize}
              page={pageIndex}
              setPage={handleChangePage}
              setRowsPerPage={handleChangeRowsPerPage}
              labelRowsPerPage={labelRowsPerPage}
            />
          </Box>
        )}
        {!isLoading && Array.isArray(data) && data.length === 0 && (
          <Grid container justifyContent="center">
            <Typography className={classes.noResults} variant="body1" gutterBottom>
              {noResultsText}
            </Typography>
          </Grid>
        )}

        {LoadMoreComponent && <LoadMoreComponent />}
      </TableContainer>
    </DragDropContext>
  );
};

const useStyles = makeStyles<
  Theme,
  {
    isUnderContractPage: boolean;
  }
>((theme: Theme) => ({
  noResults: {
    marginTop: '1rem'
  },
  stickyHeaders: {
    flexGrow: 1,
    maxHeight: '100%',
    overflow: 'visible'
  },
  borderNone: {
    border: 'none'
  },
  mobileTable: {
    display: 'block'
  },
  mobileCell: {
    padding: 0,
    border: 0
  },
  printClass: {
    '@media print': {
      height: 'auto',
      overflow: 'visible'
    }
  },
  applyStickyHeaders: {
    position: 'sticky',
    top: 100,
    zIndex: 1
  },
  returnToTopButton: {
    position: 'absolute',
    right: 5,
    top: ({ isUnderContractPage }) => (isUnderContractPage ? 25 : 13),
    zIndex: 2
  }
}));
